import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { SortingState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { z } from 'zod';

import { ClipboardList, FileCheck, FileClock, ListFilter } from '@repo/media/icons';

import { Button } from '@repo/components/button';
import { Callout } from '@repo/components/callout';
import { Card } from '@repo/components/card';
import { DataCard } from '@repo/components/data-card';
import { DateRangePicker } from '@repo/components/date-picker';
import { parseDateRange, serializeDateRange } from '@repo/components/date-picker/utils';
import type { DateRange } from '@repo/components/date-picker/utils';
import { LoadingIndicator } from '@repo/components/loading-indicator';
import { Menu, MenuItem, MenuSection, MenuTrigger } from '@repo/components/menu';
import { Pagination } from '@repo/components/pagination';
import { Popover } from '@repo/components/popover';
import { SearchField } from '@repo/components/search-field';

import { PageLoading } from '@repo/features/page-loading';
import { addToast } from '@repo/features/toast';

import { useFeatureGuard } from '~/features/auth/use-feature-guard.ts';
import { ContentHeader } from '~/features/content-header/content-header.tsx';
import { PageError } from '~/features/page-error/page-error.tsx';
import { queryProjectSchema } from '~/services/composition/composition.ts';
import {
  createDeclaration,
  deleteDeclaration,
  getDeclarationPdfUrl,
  queryCargosWithCompositionStatus,
} from '~/services/declaration/declaration.ts';

import { CancelDeclarationDialog } from './-components/cancel-declaration-dialog.tsx';
import { DeclarationsTable } from './-components/declarations-table.tsx';
import { DEFAULT_SORT, SORT_OPTIONS, STATUS_OPTIONS } from './-consts.ts';

const searchSchema = z.object({
  sortBy: z.enum(SORT_OPTIONS).catch(DEFAULT_SORT),
  status: z.enum(Object.values(STATUS_OPTIONS).map(({ key }) => key)).catch(STATUS_OPTIONS.all.key),
  search: z.string().catch(''),
  dateRange: z
    .object({
      start: z.iso.date(),
      end: z.iso.date(),
    })
    .optional()
    .catch(undefined),
  page: z.number().min(0).catch(0),
  includeCompleted: z.boolean().catch(false),
});
type SearchParams = z.infer<typeof searchSchema>;

export const Route = createFileRoute('/project/$project/(declarations)/declarations/')({
  validateSearch: searchSchema.catch(() => searchSchema.parse({})),
  component: DeclarationsOverview,
  loader: async ({ context: { queryClient }, params: { project } }) => {
    const schema = await queryClient.ensureQueryData(queryProjectSchema(project));
    // Schema exists = project is configured. Previously we checked status === 'active',
    // but the singular schema has no lifecycle status.
    if (schema && schema.items.length > 0) {
      await queryClient.ensureQueryData(queryCargosWithCompositionStatus(project, false));
    }
  },
});

const PAGE_SIZE = 25;

function CompositionSetupBlocked({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="@container flex flex-col items-start gap-6 pb-6">
      <div className="space-y-1 pb-4 w-full border-b border-b-secondary-200">
        <ContentHeader>Declarations</ContentHeader>
        <p className="text-secondary-500">Site composition declarations are available once internal setup is complete.</p>
      </div>

      <Callout variant="warning" title={title}>
        <p>{description}</p>
      </Callout>
    </div>
  );
}

function DeclarationsOverview() {
  const { project } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const routeNavigate = Route.useNavigate();
  const queryClient = useQueryClient();

  const { hasFeature } = useFeatureGuard();
  const canDeclare = hasFeature({ key: 'composition:declare', scope: 'Project' });

  const schemaQuery = useQuery(queryProjectSchema(project));
  const isSchemaReady = (schemaQuery.data?.items.length ?? 0) > 0;

  const {
    data: cargos,
    isFetching,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    ...queryCargosWithCompositionStatus(project, search.includeCompleted),
    enabled: isSchemaReady,
  });

  const [searchTerm, setSearchTerm] = useState(search.search);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const [dateRange, setDateRange] = useState<DateRange | null>(() => parseDateRange(search.dateRange));

  const [pageCount, setPageCount] = useState(0);
  const [filteredDataCount, setFilteredDataCount] = useState(cargos?.length ?? 0);

  const createMutation = useMutation({
    mutationFn: ({ cargoId, isPreliminary }: { cargoId: string; isPreliminary: boolean }) => createDeclaration(project, cargoId, null, isPreliminary),
    onSuccess: (declaration) => {
      void queryClient.invalidateQueries({ queryKey: [project, 'declarations'] });
      addToast({
        variant: 'positive',
        title: declaration.isPreliminary ? 'Preliminary declaration created' : 'Final declaration created',
      });
      void navigate({
        to: '/project/$project/declarations/$declaration',
        params: { project, declaration: declaration.id },
      });
    },
    onError: (error) => {
      addToast({
        variant: 'negative',
        title: 'Failed to create declaration',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  const [cancelTarget, setCancelTarget] = useState<{ declarationId: string; cargoId: string } | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (declarationId: string) => deleteDeclaration(project, declarationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [project, 'declarations'] });
      addToast({ variant: 'positive', title: 'Declaration cancelled' });
      setCancelTarget(null);
    },
    onError: (error) => {
      addToast({
        variant: 'negative',
        title: 'Failed to cancel declaration',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  const pdfMutation = useMutation({
    mutationFn: ({ projectId, declarationId }: { projectId: string; declarationId: string }) => getDeclarationPdfUrl(projectId, declarationId),
    onSuccess: (url) => {
      window.open(url, '_blank');
    },
    onError: (error) => {
      addToast({
        variant: 'negative',
        title: 'Failed to download PDF',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  const stats = useMemo(() => {
    if (!cargos) {
      return { total: 0, pending: 0, declared: 0 };
    }

    return cargos.reduce(
      (acc, cargo) => {
        acc.total++;
        if (cargo.compositionStatus === 'Declared') {
          acc.declared++;
        }
        else {
          acc.pending++;
        }
        return acc;
      },
      { total: 0, pending: 0, declared: 0 },
    );
  }, [cargos]);

  const updateSearchParams = useCallback(
    (params: Partial<SearchParams>) => {
      void routeNavigate({ to: '.', search: prev => ({ ...prev, ...params }), replace: true, resetScroll: false });
    },
    [routeNavigate],
  );

  const clearFilter = () => {
    updateSearchParams({ status: 'all', dateRange: undefined, page: 0, search: '' });
    setDateRange(null);
    setSearchTerm('');
  };

  const pagination = useMemo(() => ({
    pageIndex: search.page,
    pageSize: PAGE_SIZE,
  }), [search.page]);

  const columnFilters = useMemo(
    () => [
      {
        id: 'createdAt',
        value: {
          from: search.dateRange?.start ? new Date(search.dateRange.start) : undefined,
          to: search.dateRange?.end ? new Date(search.dateRange.end) : undefined,
        },
      },
      ...(search.status !== 'all'
        ? [{
            id: 'compositionStatus',
            value: search.status,
          }]
        : []),
    ],
    [search.dateRange, search.status],
  );

  const sortingState = useMemo((): SortingState => {
    const [id = '', dir] = search.sortBy.split(':');
    return [{ id, desc: dir === 'desc' }];
  }, [search.sortBy]);

  useEffect(() => {
    const serializedDateRange = serializeDateRange(dateRange);
    const searchChanged = debouncedSearchTerm !== search.search;
    const dateChanged = search.dateRange?.start !== serializedDateRange?.start
      || search.dateRange?.end !== serializedDateRange?.end;

    if (searchChanged || dateChanged) {
      updateSearchParams({
        search: debouncedSearchTerm,
        dateRange: serializedDateRange,
        page: 0,
      });
    }
  }, [dateRange, debouncedSearchTerm, search.search, search.dateRange, updateSearchParams]);

  const renderPaginationInfo = () => {
    if (pageCount <= 1) {
      return filteredDataCount;
    }

    const start = search.page * PAGE_SIZE + 1;
    const end = Math.min(search.page * PAGE_SIZE + PAGE_SIZE, filteredDataCount);

    return `${start}-${end}`;
  };

  if (schemaQuery.isLoading || (isSchemaReady && isLoading)) {
    return <PageLoading />;
  }

  if (schemaQuery.isError) {
    return (
      <PageError
        msg="An error occurred while loading composition setup."
        retry={() => { void schemaQuery.refetch(); }}
      />
    );
  }

  if (!schemaQuery.data || schemaQuery.data.items.length === 0) {
    return (
      <CompositionSetupBlocked
        title="Composition schema is not configured"
        description="This site does not have composition schema components configured yet. An internal user must add components to the schema in the internal application before declarations can be created."
      />
    );
  }

  if (isError || !cargos) {
    return (
      <PageError
        msg="An error occurred while loading declarations."
        retry={() => {
          void refetch();
        }}
      />
    );
  }

  return (
    <div className="@container flex flex-col items-start gap-6 pb-6">
      <div className="space-y-1 pb-4 w-full border-b border-b-secondary-200">
        <ContentHeader>Declarations</ContentHeader>
        <div className="flex items-center justify-between gap-4">
          <p className="text-secondary-500">
            Declare CO2 composition for scheduled cargos before loading operations
          </p>
          <div className="flex items-center gap-2">
            {isFetching && !isLoading && <LoadingIndicator size={16} className="text-primary-700" />}
          </div>
        </div>
      </div>

      <div className={twMerge('grid grid-cols-1 gap-4 w-full', '@xl:grid-cols-3')}>
        <DataCard
          label="Scheduled cargos"
          value={String(stats.total)}
          icon={ClipboardList}
        />
        <DataCard
          label="Pending declarations"
          value={String(stats.pending)}
          icon={FileClock}
        />
        <DataCard
          label="Declared"
          value={String(stats.declared)}
          icon={FileCheck}
        />
      </div>

      <Card className="w-full gap-4">
        <div className="grid w-full gap-4 @xl:grid-cols-[minmax(0,1fr)_auto_auto]">
          <SearchField
            aria-label="Search declarations"
            placeholder="Search by cargo or voyage ID"
            value={searchTerm}
            onChange={setSearchTerm}
          />

          <DateRangePicker
            aria-label="Filter by cargo created date"
            value={dateRange}
            onChange={setDateRange}
          />

          <div className="flex gap-3">
            <MenuTrigger>
              <Button
                variant="soft"
                icon={{ component: <ListFilter size={16} /> }}
              >
                Status:
                {' '}
                {STATUS_OPTIONS[search.status].title}
              </Button>
              <Popover>
                <Menu
                  selectionMode="single"
                  selectedKeys={[search.status]}
                  onSelectionChange={(keys) => {
                    const key = Array.from(keys)[0];
                    if (typeof key === 'string') {
                      updateSearchParams({ status: key as SearchParams['status'], page: 0 });
                    }
                  }}
                >
                  <MenuSection title="Filter by status">
                    {Object.values(STATUS_OPTIONS).map(option => (
                      <MenuItem key={option.key} id={option.key}>
                        {option.title}
                      </MenuItem>
                    ))}
                  </MenuSection>
                </Menu>
              </Popover>
            </MenuTrigger>

            <Button variant="ghost" color="secondary" action={clearFilter}>
              Clear filters
            </Button>
          </div>
        </div>

        <DeclarationsTable
          cargos={cargos}
          clearFilter={clearFilter}
          columnFilters={columnFilters}
          sorting={sortingState}
          setSorting={(nextSort) => {
            updateSearchParams({ sortBy: nextSort, page: 0 });
          }}
          globalFilter={search.search}
          pagination={pagination}
          setFilteredDataCount={setFilteredDataCount}
          setPageCount={setPageCount}
          canDeclare={canDeclare}
          onCreateDeclaration={(cargoId, isPreliminary) => {
            createMutation.mutate({ cargoId, isPreliminary });
          }}
          isCreating={createMutation.isPending}
          onCancelDeclaration={(declarationId, cargoId) => {
            setCancelTarget({ declarationId, cargoId });
          }}
          isCancelling={deleteMutation.isPending}
          onDownloadPdf={(projectId, declarationId) => {
            pdfMutation.mutate({ projectId, declarationId });
          }}
          isDownloadingPdf={pdfMutation.isPending}
        />

        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-secondary-500">
            Showing
            {' '}
            {renderPaginationInfo()}
            {' '}
            of
            {' '}
            {filteredDataCount}
          </p>
          <Pagination
            currentPage={search.page + 1}
            pageCount={pageCount}
            onChange={(page: number) => updateSearchParams({ page: page - 1 })}
          />
        </div>
      </Card>

      <CancelDeclarationDialog
        isOpen={cancelTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deleteMutation.isPending) {
            setCancelTarget(null);
          }
        }}
        onConfirm={() => {
          if (!cancelTarget) {
            return;
          }
          deleteMutation.mutate(cancelTarget.declarationId);
        }}
        isPending={deleteMutation.isPending}
        cargoId={cancelTarget?.cargoId ?? null}
      />
    </div>
  );
}
